// Define application steps for navigation
export enum AppStep {
  HOME = 'HOME',
  AUTH = 'AUTH',
  CHOOSE_ASSESSMENT = 'CHOOSE_ASSESSMENT',
  CREATE_ASSESSMENT = 'CREATE_ASSESSMENT',
  UPLOAD_ESSAY_DETAILS = 'UPLOAD_ESSAY_DETAILS',
  PROCESSING = 'PROCESSING',
  SHOW_RESULTS = 'SHOW_RESULTS',
  ADMIN_DASHBOARD = 'ADMIN_DASHBOARD',
  ACCOUNT = 'ACCOUNT',
  ABOUT = 'ABOUT',
  HELP_CONTACT = 'HELP_CONTACT',
  CONTACT_US = 'CONTACT_US',
  PRIVACY_POLICY = 'PRIVACY_POLICY',
  REFUND_POLICY = 'REFUND_POLICY',
  TERMS_OF_SERVICE = 'TERMS_OF_SERVICE',
}

// Assessment interfaces  
export interface Assessment {
  id: string;
  name: string;
  title: string; // For display purposes
  description?: string; // Optional description for display
  question: string;
  rubric: string;
  systemInstructions?: string;
  criteria?: Array<{
    name: string;
    weight: number;
    bands?: Array<{
      min: number;
      max: number;
      descriptor: string;
    }>;
  }>;
  totalMarks?: number;
  yearLevel?: string;
  // New attributes
  subject?: string;
  topic?: string;
  yearGroup?: string;
  year?: string;
  examCondition?: string;
  assessmentType?: string;
  archived?: boolean;
  assessmentNumber?: number; // Auto-incrementing assessment number
  // Word count range
  minWordCount?: number;
  maxWordCount?: number;
  exemplars?: Array<{
    essayText: string;
    yearLevel?: string;
    criteriaBands: Array<{
      name: string;
      band: string;
    }>;
    overallScore: number;
    explanation?: string;
  }>;
  createdAt?: any;
  userId?: string;
}

// Rubric template interfaces (saved marking rubrics reusable across assessments)
export interface RubricTemplateBand {
  min: number;
  max: number;
  descriptor: string;
}

export interface RubricTemplateCriterion {
  name: string;
  weight: number; // percent; all weights sum to 100
  bands: RubricTemplateBand[];
}

export interface RubricTemplate {
  id: string;
  userId: string;
  name: string; // unique per user (case-insensitive)
  totalMarks: number;
  criteria: RubricTemplateCriterion[];
  sourceAssessmentId?: string; // assessment it was saved from (informational only)
  createdAt?: any;
  updatedAt?: any;
}

// Model Result interface for individual LLM results
export interface ModelResult {
  modelId: string;
  modelName: string;
  lastRunAt?: Date;
  timestamp: string | Date; // When this marking was completed
  modelVersion?: string;
  promptVersion?: string;
  rawMarkingRuns: any[];
  consolidatedMarkingResults: any[];
  finalOverallScore: number | null;
  finalOverallBand: string | null;
  finalConfidence: string | null;
  bandScores?: { [bandName: string]: number }; // Band scores for this model
  ensembleSpread: {
    min: number;
    max: number;
    stdDev: number;
  } | null;
  feedback: string | null;
  markingPromptUsed: string | null;
  justificationGenerationPromptUsed: string | null;
  metaAnalysisPromptUsed: string | null;
  scoringMethodUsed: string | null;
  scoringCalculationTrace: string | null;
}

// Essay interfaces
export interface Essay {
  id: string;
  studentName: string;
  assessmentId: string;
  userId?: string;
  uploadedImages: Array<{
    id: string;
    pageLabel: string;
    previewUrl: string;
  }>;
  transcribedText: string | null;
  wordCount?: number; // NEW: Word count for the essay
  submissionDate?: string; // Date when the essay was submitted
  
  // NEW: Multiple model results storage
  modelResults?: { [modelId: string]: ModelResult };
  
  // Manual marking results (unchanged)
  manualMarkingResults?: any[];
  manualOverallScore: number | null;
  manualBandScores?: { [bandName: string]: number }; // Manual band scores
  teacherFeedback?: string;
  varianceExplanation?: string | null;
  
  // DEPRECATED: Legacy single-model fields for backward compatibility
  // These will be migrated to modelResults and eventually removed
  rawMarkingRuns?: any[];
  consolidatedMarkingResults?: any[];
  finalOverallScore?: number | null;
  finalOverallBand?: string | null;
  finalConfidence?: string | null;
  ensembleSpread?: {
    min: number;
    max: number;
    stdDev: number;
  } | null;
  feedback?: string | null;
  markingPromptUsed?: string | null;
  justificationGenerationPromptUsed?: string | null;
  metaAnalysisPromptUsed?: string | null;
  modelUsed?: string | null;
  scoringMethodUsed?: string | null;
  scoringCalculationTrace?: string | null;
  
  inputType?: string | null;
  createdAt?: any;
}

// Model configuration
export interface Model {
  id: string;
  name: string;
  apiName: string;
  maxInputTokens: number;
  maxOutputTokens: number;
  temperature: number;
  topP: number;
  topK: number;
  version?: string;
}

// Credit System Types
export interface CreditWallet {
  id: string;
  ownerType: 'user' | 'org';
  ownerId: string;
  balance: number;
  status: 'active' | 'hold' | 'suspended';
  lifetimeSpend: number; // in cents
  createdAt: any;
  updatedAt: any;
}

export interface CreditTransaction {
  id: string;
  walletId: string;
  type: 'purchase' | 'consume' | 'refund' | 'grant' | 'adjustment';
  amount: number;
  balanceAfter: number;
  currency: string;
  relatedObjectType?: string;
  relatedObjectId?: string;
  memo?: string;
  actorId?: string;
  actorType?: string;
  createdAt: any;
}

export interface CreditPack {
  id: string;
  name: string;
  credits: number;
  stripeProductId: string;
  stripePriceId: string;
  audPriceCents: number;
  active: boolean;
  createdAt: any;
  updatedAt: any;
}

export interface CreditCost {
  id: string;
  actionType: string;
  creditsCost: number;
  version: number;
  activeFrom: any;
  activeTo?: any;
}

export interface PaymentIntent {
  id: string;
  stripePaymentIntentId: string;
  userId: string;
  packId: string;
  status: 'pending' | 'succeeded' | 'failed' | 'canceled';
  amount: number;
  currency: string;
  createdAt: any;
  updatedAt: any;
}

// Error handling
export interface AppError {
  message: string;
  code?: string;
  details?: any;
}
